// Eskom PARCC — typed READ data layer over Firebase Data Connect.
//
// This module is the application's single source of *reads*: it calls the
// generated SDK query functions (pull*/…ByRef), then maps each raw Data Connect
// row into a clean, typed domain object (src/data/types). It deliberately does
// NOT reproduce the monolith's `raw{}` sheet-header map — read surfaces consume
// typed fields only. Writes, mutations and optimistic concurrency are Phase 3.
//
// Each mapper (`mapTransactionRow`, `mapFindingRow`, `mapCesRow`,
// `mapWorkingPaperRow`) is a small pure function so it can be unit-tested
// without touching Firebase. The fetch* functions wire those mappers to the
// generated SDK + the shared `dc` instance.
import {
  pullTransactions,
  pullTransactionsForManage,
  pullFindings,
  pullCesRows,
  pullWorkingPapers,
  transactionByRef,
  accessGrantByEmail,
  listAccessGrants,
  nameDirectory,
  pullAccessAuditLog,
  transactionAuditTrail,
  auditLogWindow,
  evaluationSnapshotsForTx,
  evaluationSnapshotDetail,
  pullReportRuns,
  pullRfiRegister,
  pullRtmSignoff,
  pullReportNarratives,
  pullRtmStatus,
  pullMyNotifications,
  pullQaNotes,
  type PullTransactionsData,
  type PullFindingsData,
  type PullCesRowsData,
  type PullWorkingPapersData,
  type PullRfiRegisterData,
  type PullRtmSignoffData,
  type PullReportNarrativesData,
  type PullRtmStatusData,
  type PullMyNotificationsData,
  type PullQaNotesData,
  type PullReportRunsData,
} from '@eskom-parcc/dataconnect';
import type { RfiRow, RtmSignoffRow } from '../data/types';
import { dc } from './firebase';
import { adminApi } from './adminApi';
import { canonicaliseComplianceLevel } from '../domain/compliance';
import { normalizeEmail } from './email';
import { assertUnderReadCap } from './readCaps';
import type { Transaction, Finding, CesRow, WorkingPaper } from '../data/types';
import type { RuleReferences } from '../domain/rulebook/ruleReferences';
import type { FieldChange } from './fieldDiff';
import { type CesStatus, CES_STATUS_OPTIONS } from '../domain/enums';

// --- Raw Data Connect row shapes -------------------------------------------
// Narrowed element types of the generated *Data payloads, so the pure mappers
// have a precise input contract without re-deriving the whole SDK type. Trust
// these to the generated index.d.ts; they are the array element of each query.
export type DcTransactionRow = PullTransactionsData['transactions'][number];
export type DcFindingRow = PullFindingsData['findings'][number];
export type DcCesRow = PullCesRowsData['cesRows'][number];
export type DcWorkingPaperRow = PullWorkingPapersData['workingPapers'][number];

// --- Read-projection types -------------------------------------------------
// The pull queries surface only the linked `transaction.referenceNo`, never the
// transaction UUID, and they do not select the per-row concurrency token. The
// domain types model `transactionId` (a write key) and `rowVersion` (an
// optimistic-concurrency token) as required fields, but neither is resolved on
// a read. Rather than fill them with misleading placeholders ('' / 0) — which a
// writer could pass straight into a mutation as a stale/empty precondition — we
// OMIT them from the read shape. To obtain real write preconditions for a
// reference, call {@link resolveTransactionRef}.

/**
 * A finding as read from the pull query: the domain {@link Finding} minus the
 * unresolved write key (`transactionId`) and concurrency token (`rowVersion`).
 * Writers: obtain real values via {@link resolveTransactionRef}.
 */
export type FindingRead = Omit<Finding, 'transactionId' | 'rowVersion'>;

/**
 * A CES row as read from the pull query: the domain {@link CesRow} minus the
 * unresolved write key (`transactionId`). The domain `CesRow` carries no
 * `rowVersion`, so nothing else is omitted.
 * Writers: obtain a real `transactionId` via {@link resolveTransactionRef}.
 */
export type CesRowRead = Omit<CesRow, 'transactionId'>;

/**
 * A working paper as read from the pull query: the domain {@link WorkingPaper}
 * minus the unresolved write key (`transactionId`) and concurrency token
 * (`rowVersion`), plus the `referenceNo` join key the pull query does surface.
 * Writers: obtain real `{ id, rowVersion }` via {@link resolveTransactionRef}.
 */
export type WorkingPaperRead = Omit<WorkingPaper, 'transactionId' | 'rowVersion'> & {
  referenceNo: string;
};

// --- Small coercion helpers ------------------------------------------------

/** Drop nullish; otherwise return the value (keeps optional domain fields tidy). */
function opt<T>(v: T | null | undefined): T | undefined {
  return v == null ? undefined : v;
}

/** Normalise an optional email; an absent/blank email collapses to undefined. */
function optEmail(v: string | null | undefined): string | undefined {
  const e = normalizeEmail(v);
  return e === '' ? undefined : e;
}

/** Coalesce a nullable string to '' (for domain fields typed as plain string). */
function str(v: string | null | undefined): string {
  return v == null ? '' : v;
}

/**
 * Parse the JSON-serialised regulatory references stored on a finding back into
 * {@link RuleReferences}. Tolerant: a null/blank/malformed value yields undefined
 * (never throws), and only the three known string-array fields are surfaced.
 */
function parseRefs(v: string | null | undefined): RuleReferences | undefined {
  if (!v) return undefined;
  try {
    const obj = JSON.parse(v) as Partial<RuleReferences>;
    if (!obj || typeof obj !== 'object') return undefined;
    const arr = (x: unknown): string[] =>
      Array.isArray(x) ? x.filter((s): s is string => typeof s === 'string') : [];
    const refs: RuleReferences = {
      eskomPolicy: arr(obj.eskomPolicy),
      pfma: arr(obj.pfma),
      law: arr(obj.law),
    };
    return refs.eskomPolicy.length || refs.pfma.length || refs.law.length ? refs : undefined;
  } catch {
    return undefined;
  }
}

// --- Narrow-union guards ---------------------------------------------------
// The generated SDK types `confidence` and `status` as free-form strings, but
// the domain models them as closed unions. Guard the casts at the boundary so a
// malformed/unexpected payload value can never masquerade as a valid member.

/** The closed set of allowed Finding.confidence values. */
const CONFIDENCE_VALUES = new Set<Finding['confidence']>(['High', 'Medium', 'Low']);

/** The canonical CesStatus set (incl. '' for unset), from the domain enums. */
const CES_STATUS_VALUES = new Set<CesStatus>(CES_STATUS_OPTIONS);

/** Accept a confidence string only if it is a real union member, else undefined. */
function confidenceOrUndefined(v: string | null | undefined): Finding['confidence'] {
  return CONFIDENCE_VALUES.has(v as Finding['confidence']) ? (v as Finding['confidence']) : undefined;
}

/** Accept a status string only if it is a real CesStatus member, else '' (unset). */
function cesStatusOrEmpty(v: string | null | undefined): CesStatus {
  return CES_STATUS_VALUES.has(v as CesStatus) ? (v as CesStatus) : '';
}

// --- Pure row mappers ------------------------------------------------------

/** Map a Data Connect transaction row → domain {@link Transaction}. */
export function mapTransactionRow(row: DcTransactionRow): Transaction {
  return {
    id: row.id,
    referenceNo: row.referenceNo,
    baseNo: opt(row.baseNo),
    recordLabel: opt(row.recordLabel),
    // dataset is non-optional in the domain type; default to 'Other' when unset.
    dataset: row.dataset ?? 'Other',
    year: opt(row.year),
    period: opt(row.period),
    division: opt(row.division),
    supplier: opt(row.supplier),
    csdNumber: opt(row.csdNumber),
    // value is non-null in the domain type; a missing value reads as 0.
    value: row.value ?? 0,
    valueDisplay: opt(row.valueDisplay),
    mechanism: opt(row.mechanism),
    reviewed: opt(row.reviewed),
    onDashboard: opt(row.onDashboard),
    panel: opt(row.panel),
    gapsStatus: opt(row.gapsStatus),
    complianceLevel: canonicaliseComplianceLevel(opt(row.complianceLevel)),
    complianceLevelOverride: canonicaliseComplianceLevel(opt(row.complianceLevelOverride)),
    selfDeclaration: opt(row.selfDeclaration),
    initialFindings: opt(row.initialFindings),
    updatedFindings: opt(row.updatedFindings),
    findingText: opt(row.findingText),
    link: opt(row.link),
    reviewStatus: opt(row.reviewStatus),
    progress: opt(row.progress),
    workingPaperNotes: opt(row.workingPaperNotes),
    assignedToEmail: optEmail(row.assignedToEmail),
    assignedByEmail: optEmail(row.assignedByEmail),
    assignedAt: opt(row.assignedAt),
    reviewerEmail: optEmail(row.reviewerEmail),
    reviewedAt: opt(row.reviewedAt),
    capturedByEmail: optEmail(row.capturedByEmail),
    capturedDate: opt(row.capturedDate),
    ou: opt(row.ou),
    contractNumber: opt(row.contractNumber),
    cirNumber: opt(row.cirNumber),
    linkProvided: row.linkProvided ?? undefined,
    linkIssue: opt(row.linkIssue),
    bu: opt(row.bu),
    briefDescription: opt(row.briefDescription),
    buyer: opt(row.buyer),
    prNumber: opt(row.prNumber),
    procurementManager: opt(row.procurementManager),
    endUser: opt(row.endUser),
    rowVersion: row.rowVersion,
  };
}

// --- RFI Register + RTM sign-off (owner feedback point 4) ------------------

/** Map a Data Connect RFI row → {@link RfiRow}. */
export function mapRfiRow(row: PullRfiRegisterData['rfiRegisters'][number]): RfiRow {
  return {
    id: row.id,
    rfiRef: opt(row.rfiRef),
    division: opt(row.division),
    supplier: opt(row.supplier),
    contractNumber: opt(row.contractNumber),
    contractValue: opt(row.contractValue),
    dateReceivedClient: opt(row.dateReceivedClient),
    dateReceivedConsultant: opt(row.dateReceivedConsultant),
    dateSubmittedDeloitte: opt(row.dateSubmittedDeloitte),
    concerns: opt(row.concerns),
    status: opt(row.status),
  };
}

/** Fetch the RFI register (admin-only query). */
export async function fetchRfiRegister(): Promise<RfiRow[]> {
  const { data } = await pullRfiRegister(dc);
  return data.rfiRegisters.map(mapRfiRow);
}

/** Map a Data Connect RTM sign-off row → {@link RtmSignoffRow}. */
export function mapRtmRow(row: PullRtmSignoffData['rtmSignoffs'][number]): RtmSignoffRow {
  return {
    division: row.division,
    draftSubmitted: row.draftSubmitted,
    finalSubmitted: row.finalSubmitted,
    signedOff: row.signedOff,
    pendingSignoff: row.pendingSignoff,
    note: opt(row.note),
  };
}

/** Fetch the per-division RTM sign-off status (admin-only query). */
export async function fetchRtmSignoff(): Promise<RtmSignoffRow[]> {
  const { data } = await pullRtmSignoff(dc);
  return data.rtmSignoffs.map(mapRtmRow);
}

// --- Server-scoped read for non-admins (owner feedback point 7) ------------

/** A non-admin user's own register, fetched through the admin-api scoped proxy. */
export interface ScopedRegister {
  transactions: Transaction[];
  findings: FindingRead[];
  workingPapers: WorkingPaperRead[];
}

/**
 * Fetch the signed-in non-admin's register via the server-side scoped proxy
 * (`/api/scoped/register`), which filters by the caller's grant re-derived from
 * the verified token (division visibility ∪ own allocations). The filter itself is
 * server-enforced and cannot be widened by the client; note, however, that the
 * underlying Pull* connector queries are still @auth(level: USER) (see the proxy
 * comment in admin-api), so this is the app's read path, not yet an absolute
 * connector-level boundary. Rows match the Pull* shapes, so the mappers apply.
 */
export async function fetchMyRegister(): Promise<ScopedRegister> {
  const data = await adminApi.scopedRegister();
  return {
    transactions: (data.transactions as DcTransactionRow[]).map(mapTransactionRow),
    findings: (data.findings as DcFindingRow[]).map(mapFindingRow),
    workingPapers: (data.workingPapers as DcWorkingPaperRow[]).map(mapWorkingPaperRow),
  };
}

/**
 * Map a Data Connect finding row → {@link FindingRead}.
 *
 * The pull query exposes the link as `transaction.referenceNo` (with a
 * row-level `referenceNo` fallback) but not the transaction UUID, and it does
 * not select `rowVersion`; both are therefore omitted from the read projection
 * rather than filled with placeholders. Writers resolve them via
 * {@link resolveTransactionRef}.
 */
export function mapFindingRow(row: DcFindingRow): FindingRead {
  // `?.` guards a malformed payload: the SDK types `transaction` as non-nullable.
  const ref = row.transaction?.referenceNo || row.referenceNo || '';
  return {
    id: row.id,
    referenceNo: ref,
    text: row.text,
    source: opt(row.source),
    legacyStatus: opt(row.legacyStatus),
    anchors: row.anchors ?? [],
    suggestedCompliance: canonicaliseComplianceLevel(opt(row.suggestedCompliance)),
    finalCompliance: canonicaliseComplianceLevel(opt(row.finalCompliance)),
    rationale: opt(row.rationale),
    confidence: confidenceOrUndefined(row.confidence),
    route: opt(row.route),
    matchedRules: row.matchedRules ?? [],
    effectiveStatus: opt(row.effectiveStatus),
    reviewNotes: opt(row.reviewNotes),
    regulatoryRefs: parseRefs(row.regulatoryRefs),
    criterion: opt(row.criterion),
    // AI provenance — now READ, so reports can surface it. aiCitations stays a
    // raw JSON string (the report parses it), mirroring how it was serialised on
    // save. isAiAssisted is the derived flag every reporting surface keys off.
    aiPfmaSection: opt(row.aiPfmaSection),
    aiCitations: opt(row.aiCitations),
    aiModel: opt(row.aiModel),
    aiRunId: opt(row.aiRunId),
    isAiAssisted: row.aiRunId != null,
    seeded: row.seeded,
    createdAt: opt(row.createdAt),
    // Gap lifecycle (RTM Workstream I) — so surfaces can show when a gap was first
    // raised and how it was closed; carried through the editor so a re-save
    // preserves the closure provenance rather than re-stamping it.
    firstRaisedAt: opt(row.firstRaisedAt),
    closedAt: opt(row.closedAt),
    closedVia: opt(row.closedVia),
  };
}

/**
 * Map a Data Connect CES row → {@link CesRowRead}.
 *
 * As with findings, only `transaction.referenceNo` is exposed (no UUID), so the
 * unresolved write key `transactionId` is omitted from the read projection
 * (writers resolve it via {@link resolveTransactionRef}). The nullable text
 * columns coalesce to '' because the domain type models them as plain strings;
 * `status` is validated against the canonical CesStatus set.
 */
export function mapCesRow(row: DcCesRow): CesRowRead {
  return {
    id: row.id,
    seq: row.seq,
    stage: row.stage,
    criterion: row.criterion,
    status: cesStatusOrEmpty(row.status),
    finding: str(row.finding),
  };
}

/**
 * Map a Data Connect working-paper row → {@link WorkingPaperRead}.
 *
 * `rows` starts empty: CES rows are pulled separately (pullCesRows) and stitched
 * in by ref when a surface needs the full paper. The transaction UUID and
 * `rowVersion` are not surfaced by this query, so they are omitted from the read
 * projection (writers resolve them via {@link resolveTransactionRef}). The paper
 * carries its `referenceNo` link instead.
 */
export function mapWorkingPaperRow(row: DcWorkingPaperRow): WorkingPaperRead {
  return {
    id: row.id,
    // `?.` guards a malformed payload: the SDK types `transaction` as non-nullable.
    referenceNo: row.transaction?.referenceNo || '',
    progress: opt(row.progress),
    complianceLevel: canonicaliseComplianceLevel(opt(row.complianceLevel)),
    findingText: opt(row.findingText),
    workingPaperNotes: opt(row.workingPaperNotes),
    reviewerEmail: optEmail(row.reviewerEmail),
    reviewerName: opt(row.reviewerName),
    reviewStatus: opt(row.reviewStatus),
    reviewedAt: opt(row.reviewedAt),
    capturedByEmail: optEmail(row.capturedByEmail),
    capturedDate: opt(row.capturedDate),
    // NB: the query selects `assignedToEmail`, but allocation/assignment lives on
    // Transaction + Allocation, not the domain WorkingPaper — so it is not mapped here.
    rows: [],
  };
}

// --- Grouping helpers ------------------------------------------------------

/**
 * Group raw CES rows by their transaction `referenceNo`, mapping each to a
 * {@link CesRowRead} and sorting each group ascending by `seq`. Takes the raw
 * rows directly (the mapped read row does not carry the reference), so there is
 * no parallel-array alignment to keep in sync. Rows with a blank/missing
 * reference are dropped. The sort is stable (Array.prototype.sort), so rows that
 * share a `seq` retain their original pull order.
 */
export function groupCesRowsByRef(rawRows: DcCesRow[]): Map<string, CesRowRead[]> {
  const grouped = new Map<string, CesRowRead[]>();
  for (const raw of rawRows) {
    // `?.` guards a malformed payload: the SDK types `transaction` as non-nullable.
    const ref = raw.transaction?.referenceNo || '';
    if (!ref) continue;
    const row = mapCesRow(raw);
    const bucket = grouped.get(ref);
    if (bucket) bucket.push(row);
    else grouped.set(ref, [row]);
  }
  for (const bucket of grouped.values()) {
    bucket.sort((a, b) => a.seq - b.seq);
  }
  return grouped;
}

// --- Fetchers (Firebase-bound) ---------------------------------------------
// Every whole-population pull passes through assertUnderReadCap: a result that lands
// exactly on its connector `limit` is REFUSED rather than mapped, because Data Connect
// gives no other sign that rows were cut and a truncated register corrupted saves in
// Sep 2026 (readCaps.ts has the record). The caps live there, pinned to queries.gql.

/** Fetch all transactions, mapped to domain {@link Transaction}s. */
export async function fetchTransactions(): Promise<Transaction[]> {
  const { data } = await pullTransactions(dc);
  return assertUnderReadCap('transactions', data.transactions).map(mapTransactionRow);
}

/**
 * Fetch the WHOLE population INCLUDING soft-deleted rows for the admin Manage
 * screen, preserving each row's `deleted` flag (the standard mapper drops it, as
 * every other read filters deleted out). Admin-only at the connector.
 */
export async function fetchTransactionsForManage(): Promise<Transaction[]> {
  const { data } = await pullTransactionsForManage(dc);
  return assertUnderReadCap('transactionsForManage', data.transactions).map((row) => ({
    ...mapTransactionRow(row),
    deleted: row.deleted,
  }));
}

/** Fetch all findings, mapped to {@link FindingRead}s. */
export async function fetchFindings(): Promise<FindingRead[]> {
  const { data } = await pullFindings(dc);
  return assertUnderReadCap('findings', data.findings).map(mapFindingRow);
}

/** Fetch all CES rows, mapped to {@link CesRowRead}s (flat, ungrouped). */
export async function fetchCesRows(): Promise<CesRowRead[]> {
  const { data } = await pullCesRows(dc);
  return assertUnderReadCap('cesRows', data.cesRows).map(mapCesRow);
}

/**
 * Fetch CES rows already grouped by transaction `referenceNo`, each group
 * sorted by `seq` — the shape working-paper / register surfaces consume.
 */
export async function fetchCesRowsByRef(): Promise<Map<string, CesRowRead[]>> {
  const { data } = await pullCesRows(dc);
  return groupCesRowsByRef(assertUnderReadCap('cesRows', data.cesRows));
}

/** Fetch all working papers, keyed by transaction `referenceNo`. */
export async function fetchWorkingPapers(): Promise<WorkingPaperRead[]> {
  const { data } = await pullWorkingPapers(dc);
  return assertUnderReadCap('workingPapers', data.workingPapers).map(mapWorkingPaperRow);
}

/**
 * Resolve a transaction `referenceNo` to its `{ id, rowVersion, deleted }`.
 * Pure read; it underpins the optimistic-concurrency writes AND the upsert's
 * revive decision — `deleted` tells the import path whether the resolved row is
 * genuinely soft-deleted (a real revive, review reset applies) or live (no
 * reset — WS2, 1 Jul 2026 incident). Returns null when nothing matches.
 */
export async function resolveTransactionRef(
  referenceNo: string,
): Promise<{ id: string; rowVersion: number; deleted: boolean } | null> {
  const { data } = await transactionByRef(dc, { referenceNo });
  const tx = data.transactions[0];
  return tx ? { id: tx.id, rowVersion: tx.rowVersion, deleted: tx.deleted === true } : null;
}

// --- Access list (admin-managed) -------------------------------------------
// Reads only. The access list is WRITTEN exclusively by the privileged admin
// service (admin-api, Cloud Run) via the Admin GraphQL — never from the client —
// so the browser never carries a credential that can mutate authorisation.

/** A single access grant, as read for role resolution. */
export interface AccessGrantRow {
  email: string;
  role: string;
  active: boolean;
  /** Home division (teamLead/reviewer); null for cross-division roles. */
  division: string | null;
  /** Allocation flag, meaningful for `manager` (Greg yes / George no). */
  canAllocate: boolean;
  /** Cross-division visibility for a reviewer/team lead (Lindiwe R4 #3). */
  crossDivision: boolean;
  /** AI Compliance Engine eligibility (TK Jul 2026). Fails CLOSED — see
   *  `canUseAi` in lib/permissions. */
  aiEnabled: boolean;
  displayName: string | null;
}

/**
 * Resolve a user's access grant by email — the DB tier of `resolveRole`
 * (src/lib/auth.tsx). Returns the matching grant or `null` when none exists.
 * Emails are normalised to the stored lower-cased form. Callers MUST treat a
 * thrown error as "fall through" (never a lock-out), and a `null` / inactive
 * result per the documented precedence — see resolveRole.
 */
export async function getAccessGrantByEmail(email: string): Promise<AccessGrantRow | null> {
  const { data } = await accessGrantByEmail(dc, { email: normalizeEmail(email) });
  const row = data.accessGrants[0];
  if (!row) return null;
  return {
    email: row.email,
    role: row.role,
    active: row.active,
    division: row.division ?? null,
    canAllocate: row.canAllocate ?? false,
    crossDivision: row.crossDivision ?? false,
    aiEnabled: row.aiEnabled ?? false,
    displayName: row.displayName ?? null,
  };
}

/** A full access-grant record for the admin Users screen. */
export interface AccessGrantRecord extends AccessGrantRow {
  firstName: string | null;
  lastName: string | null;
  /** Notification switches (TK Jul 2026). Null = enabled (default-on); a channel
   *  is off only on an explicit false. Written ONLY by admin-api. */
  notifyInApp: boolean | null;
  notifyEmail: boolean | null;
  createdAt: string;
  updatedAt: string | null;
  invitedBy: string | null;
}

/**
 * Fetch the full access list for the admin Users screen. Server-enforced
 * admin-only (the ListAccessGrants @auth claim check) — a non-admin token is
 * rejected, so this is not merely a client-side guard.
 */
export async function fetchAccessGrants(): Promise<AccessGrantRecord[]> {
  const { data } = await listAccessGrants(dc);
  return data.accessGrants.map((r) => ({
    email: r.email,
    role: r.role,
    active: r.active,
    division: r.division ?? null,
    canAllocate: r.canAllocate ?? false,
    crossDivision: r.crossDivision ?? false,
    aiEnabled: r.aiEnabled ?? false,
    displayName: r.displayName ?? null,
    firstName: r.firstName ?? null,
    lastName: r.lastName ?? null,
    notifyInApp: r.notifyInApp ?? null,
    notifyEmail: r.notifyEmail ?? null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt ?? null,
    invitedBy: r.invitedBy ?? null,
  }));
}

/** A name-directory row: email + whatever name parts the grant carries. */
export interface NameDirectoryRow {
  email: string;
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
}

/**
 * Fetch the active-user name directory for resolving assignee/reviewer emails to
 * display names (owner R3 feedback 1). USER-level — any signed-in user may read
 * it (see the NameDirectory @auth note). Returns only active grants.
 */
export async function fetchNameDirectory(): Promise<NameDirectoryRow[]> {
  const { data } = await nameDirectory(dc);
  return data.accessGrants.map((r) => ({
    email: r.email,
    displayName: r.displayName ?? null,
    firstName: r.firstName ?? null,
    lastName: r.lastName ?? null,
  }));
}

/** An audit-log entry for the admin activity feed and the transaction trail. */
export interface AuditLogEntry {
  id: string;
  at: string;
  /** Server-stamped actor email (the verified "by whom"); null for pre-migration rows. */
  actorEmail: string | null;
  action: string;
  entityType: string | null;
  entityId: string | null;
  note: string | null;
  /** The from/to field changes lifted from `payload.changes`; [] when the entry
   *  carried none (every entry before this deploy, plus non-edit actions). */
  changes: FieldChange[];
}

/** The raw AuditLog row shape shared by the connector ops and the admin-api proxy. */
export interface RawAuditRow {
  id: string;
  at: string;
  actorEmail?: string | null;
  action: string;
  entityType?: string | null;
  entityId?: string | null;
  note?: string | null;
  payload?: unknown;
}

/** Lift the from/to changes out of an audit payload, tolerating any legacy shape. */
function extractChanges(payload: unknown): FieldChange[] {
  if (payload && typeof payload === 'object' && Array.isArray((payload as { changes?: unknown }).changes)) {
    return (payload as { changes: FieldChange[] }).changes;
  }
  return [];
}

/**
 * Map one raw AuditLog row → {@link AuditLogEntry}. The SINGLE mapper shared by the
 * admin activity feed, the admin-direct transaction trail, and the server-scoped
 * proxy — so all three read identically (the mapper-equivalence contract).
 */
export function mapAuditRow(r: RawAuditRow): AuditLogEntry {
  return {
    id: r.id,
    at: r.at,
    actorEmail: r.actorEmail ?? null,
    action: r.action,
    entityType: r.entityType ?? null,
    entityId: r.entityId ?? null,
    note: r.note ?? null,
    changes: extractChanges(r.payload),
  };
}

/** A CES evaluation-sheet snapshot, summary only (no whole-document blobs). */
export interface SnapshotSummary {
  id: string;
  createdAt: string;
  sourceAction: string | null;
  reviewStatus: string | null;
}

/** A CES snapshot with its captured blobs, for the flyout diff. */
export interface SnapshotDetail extends SnapshotSummary {
  fullEvaluation: unknown;
  workingPaper: unknown;
}

/** The raw EvaluationSheetBackup summary row shape (connector op + proxy). */
export interface RawSnapshotRow {
  id: string;
  createdAt: string;
  sourceAction?: string | null;
  reviewStatus?: string | null;
  fullEvaluation?: unknown;
  workingPaper?: unknown;
}

/** Map one raw snapshot row → summary (shared by admin-direct + proxy). */
export function mapSnapshotSummaryRow(r: RawSnapshotRow): SnapshotSummary {
  return {
    id: r.id,
    createdAt: r.createdAt,
    sourceAction: r.sourceAction ?? null,
    reviewStatus: r.reviewStatus ?? null,
  };
}

/** Map one raw snapshot row → detail (summary + the captured blobs). */
export function mapSnapshotDetailRow(r: RawSnapshotRow): SnapshotDetail {
  return {
    ...mapSnapshotSummaryRow(r),
    fullEvaluation: r.fullEvaluation ?? null,
    workingPaper: r.workingPaper ?? null,
  };
}

/** One transaction's timeline inputs: audit entries + CES-snapshot summaries. */
export interface TransactionHistory {
  entries: AuditLogEntry[];
  snapshots: SnapshotSummary[];
}

/** The global audit feed for a date window, with the server-cap truncation flag. */
export interface AuditFeed {
  entries: AuditLogEntry[];
  truncated: boolean;
}

/** Fetch the recent audit trail (admin-only; server-enforced). */
export async function fetchAccessAuditLog(): Promise<AuditLogEntry[]> {
  const { data } = await pullAccessAuditLog(dc);
  return data.auditLogs.map(mapAuditRow);
}

/**
 * One transaction's audit trail — ADMIN DIRECT path (two admin-gated connector
 * ops). Ends in the same {@link mapAuditRow} / {@link mapSnapshotSummaryRow} as the
 * proxy below, so both produce byte-identical output (the mapper-equivalence test).
 */
export async function fetchTransactionHistory(referenceNo: string): Promise<TransactionHistory> {
  const [trail, snaps] = await Promise.all([
    transactionAuditTrail(dc, { referenceNo }),
    evaluationSnapshotsForTx(dc, { referenceNo }),
  ]);
  return {
    entries: trail.data.auditLogs.map(mapAuditRow),
    snapshots: snaps.data.evaluationSheetBackups.map(mapSnapshotSummaryRow),
  };
}

/**
 * One transaction's audit trail — NON-ADMIN, through the server-scoped proxy
 * (`/api/scoped/transaction-history`), which re-derives the caller's grant from the
 * verified token and 404s on a visibility miss. Same mappers as the admin path.
 */
export async function fetchMyTransactionHistory(referenceNo: string): Promise<TransactionHistory> {
  const data = await adminApi.transactionHistory(referenceNo);
  return {
    entries: (data.entries as RawAuditRow[]).map(mapAuditRow),
    snapshots: (data.snapshots as RawSnapshotRow[]).map(mapSnapshotSummaryRow),
  };
}

/** CES snapshot blobs (≤2) — ADMIN DIRECT. */
export async function fetchEvaluationSnapshotDetail(ids: string[]): Promise<SnapshotDetail[]> {
  const { data } = await evaluationSnapshotDetail(dc, { ids });
  return data.evaluationSheetBackups.map(mapSnapshotDetailRow);
}

/** CES snapshot blobs (≤2) — NON-ADMIN proxy (drops any the caller cannot see). */
export async function fetchMyEvaluationSnapshotDetail(ids: string[]): Promise<SnapshotDetail[]> {
  const data = await adminApi.evaluationSnapshots(ids);
  return (data.snapshots as RawSnapshotRow[]).map(mapSnapshotDetailRow);
}

/** The global audit feed for [from, to] — ADMIN DIRECT (whole window). */
export async function fetchAuditFeed(from: string, to: string): Promise<AuditFeed> {
  const { data } = await auditLogWindow(dc, { since: from, until: to });
  // 5000 = the AuditLogWindow connector cap; truncated when the window fills it.
  return { entries: data.auditLogs.map(mapAuditRow), truncated: data.auditLogs.length >= 5000 };
}

/** The global audit feed for [from, to] — NON-ADMIN proxy (transaction-scoped). */
export async function fetchMyAuditFeed(from: string, to: string): Promise<AuditFeed> {
  const data = await adminApi.auditFeed(from, to);
  return { entries: (data.entries as RawAuditRow[]).map(mapAuditRow), truncated: data.truncated === true };
}

/** A generated-report register entry for the admin Reports screen. */
export interface ReportRunEntry {
  id: string;
  type: string;
  format: string | null;
  /** Cloud Storage object key of the persisted artifact; null if only downloaded. */
  storagePath: string | null;
  generatedAt: string;
  /** Server-stamped generator (via the generatedBy relation); null for legacy rows. */
  generatedByEmail: string | null;
  parameters: Record<string, unknown> | null;
  summary: Record<string, unknown> | null;
  // Report archive (Jun 23 2026 feedback 2c) — null for legacy/unscoped runs.
  reportNumber: string | null;
  asOfDate: string | null;
  division: string | null;
}

export type DcReportRunRow = PullReportRunsData['reportRuns'][number];

/** Map a raw report-run row to the register entry (shared by the admin pull and the
 *  team-lead/manager divisional archive that comes back through admin-api). */
export function mapReportRunRow(r: DcReportRunRow): ReportRunEntry {
  return {
    id: r.id,
    type: r.type,
    format: r.format ?? null,
    storagePath: r.storagePath ?? null,
    generatedAt: r.generatedAt,
    generatedByEmail: r.generatedBy?.email ?? null,
    parameters: (r.parameters as Record<string, unknown> | null) ?? null,
    summary: (r.summary as Record<string, unknown> | null) ?? null,
    reportNumber: r.reportNumber ?? null,
    asOfDate: r.asOfDate ?? null,
    division: r.division ?? null,
  };
}

/** Fetch the register of generated reports (admin-only; server-enforced). */
export async function fetchReportRuns(): Promise<ReportRunEntry[]> {
  const { data } = await pullReportRuns(dc);
  return data.reportRuns.map(mapReportRunRow);
}

// --- In-app notifications (Jun 23 2026 feedback 4a) ------------------------

export type DcNotificationRow = PullMyNotificationsData['notifications'][number];

/** A notification as read for the in-app bell (the caller's own rows only). */
export interface NotificationRead {
  id: string;
  type: string;
  title: string;
  body: string | null;
  entityType: string | null;
  entityId: string | null;
  actorEmail: string | null;
  read: boolean;
  createdAt: string;
}

/** Pure: raw notification row → typed read projection. */
export function mapNotificationRow(r: DcNotificationRow): NotificationRead {
  return {
    id: r.id,
    type: r.type,
    title: r.title,
    body: r.body ?? null,
    entityType: r.entityType ?? null,
    entityId: r.entityId ?? null,
    actorEmail: r.actorEmail ?? null,
    read: r.read,
    createdAt: r.createdAt,
  };
}

/** Fetch the signed-in user's own notifications (self-scoped server-side). */
export async function fetchMyNotifications(): Promise<NotificationRead[]> {
  const { data } = await pullMyNotifications(dc);
  return data.notifications.map(mapNotificationRow);
}

// --- QA notes on working papers (Jun 23 2026 feedback 4a) ------------------

export type DcQaNoteRow = PullQaNotesData['qaNotes'][number];

/** A QA note as read; `referenceNo` is the parent working paper's transaction. */
export interface QaNoteRead {
  id: string;
  workingPaperId: string;
  referenceNo: string | null;
  note: string;
  addedByEmail: string | null;
  resolved: boolean;
  resolvedByEmail: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

/** Pure: raw QA-note row → typed read projection. */
export function mapQaNoteRow(r: DcQaNoteRow): QaNoteRead {
  return {
    id: r.id,
    workingPaperId: r.workingPaper?.id ?? '',
    referenceNo: r.workingPaper?.transaction?.referenceNo ?? null,
    note: r.note,
    addedByEmail: r.addedByEmail ?? null,
    resolved: r.resolved,
    resolvedByEmail: r.resolvedByEmail ?? null,
    resolvedAt: r.resolvedAt ?? null,
    createdAt: r.createdAt,
  };
}

/** Fetch all (non-deleted) QA notes; the page groups them by referenceNo. */
export async function fetchQaNotes(): Promise<QaNoteRead[]> {
  const { data } = await pullQaNotes(dc);
  return data.qaNotes.map(mapQaNoteRow);
}


// ---- Wednesday Composer: authored narratives + RTM status (Phase 1b) --------
export type DcReportNarrativeRow = PullReportNarrativesData['reportNarratives'][number];
export type DcRtmStatusRow = PullRtmStatusData['rtmStatuses'][number];

/** A persisted deck narrative (read projection). */
export interface ReportNarrativeEntry {
  id: string;
  fy: string;
  scopeKey: string;
  slideKey: string;
  division: string;
  text: string;
  draftInputs: Record<string, number> | null;
  edited: boolean;
  updatedAt: string | undefined;
  updatedBy: string | undefined;
}
/** A persisted reports-to-management status cell set (read projection). */
export interface RtmStatusEntry {
  id: string;
  fy: string;
  quarter: string;
  division: string;
  submitted: string | undefined;
  signedOff: string | undefined;
  submittedDate: string | undefined;
  signedOffDate: string | undefined;
  note: string | undefined;
  rtmReportKey: string | undefined;
  updatedAt: string | undefined;
  updatedBy: string | undefined;
}

export function mapReportNarrativeRow(row: DcReportNarrativeRow): ReportNarrativeEntry {
  return {
    id: row.id,
    fy: row.fy,
    scopeKey: row.scopeKey,
    slideKey: row.slideKey,
    division: row.division,
    text: row.text,
    draftInputs: (row.draftInputs as Record<string, number> | null | undefined) ?? null,
    edited: row.edited,
    updatedAt: opt(row.updatedAt),
    updatedBy: opt(row.updatedBy),
  };
}
export function mapRtmStatusRow(row: DcRtmStatusRow): RtmStatusEntry {
  return {
    id: row.id,
    fy: row.fy,
    quarter: row.quarter,
    division: row.division,
    submitted: opt(row.submitted),
    signedOff: opt(row.signedOff),
    submittedDate: opt(row.submittedDate),
    signedOffDate: opt(row.signedOffDate),
    note: opt(row.note),
    rtmReportKey: opt(row.rtmReportKey),
    updatedAt: opt(row.updatedAt),
    updatedBy: opt(row.updatedBy),
  };
}

/** Fetch the persisted narratives for a financial year (admin-only query). */
export async function fetchReportNarratives(fy: string): Promise<ReportNarrativeEntry[]> {
  const { data } = await pullReportNarratives(dc, { fy });
  return data.reportNarratives.map(mapReportNarrativeRow);
}
/** Fetch the persisted RTM status rows for a financial year (admin-only query). */
export async function fetchRtmStatus(fy: string): Promise<RtmStatusEntry[]> {
  const { data } = await pullRtmStatus(dc, { fy });
  return data.rtmStatuses.map(mapRtmStatusRow);
}
